import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getAccountsList } from '@/services/profile';
import { getLookupData } from '@/services/lookup';
import { useAuth } from '@/contexts/AuthProvider';

type TPartnerRole = 'EQUIPMENT_OWNER' | 'CHANNEL_PARTNER';

type TPartnerRoleData = {
  partnerRole: TPartnerRole;
  features: string[];
  timestamp: number;
};

type TPartnerRoleContext = {
  partnerRole: TPartnerRole | null;
  features: string[];
  loading: boolean;
  error: string | null;
  permissionsUpdated: boolean;
};

// localStorage keys
const PARTNER_ROLE_STORAGE_KEY = 'partner_role_data';
const LOOKUP_DATA_STORAGE_KEY = 'partner_portal_lookup_data';

// Helper functions for localStorage
const getStoredPartnerRoleData = (): TPartnerRoleData | null => {
  try {
    const stored = localStorage.getItem(PARTNER_ROLE_STORAGE_KEY);
    if (!stored) {
      return null;
    }

    const data: TPartnerRoleData = JSON.parse(stored);
    // Check if data is less than 1 hour old
    const oneHour = 60 * 60 * 1000;
    if (Date.now() - data.timestamp > oneHour) {
      localStorage.removeItem(PARTNER_ROLE_STORAGE_KEY);
      return null;
    }

    return data;
  } catch {
    return null;
  }
};

const setStoredPartnerRoleData = (data: TPartnerRoleData): void => {
  try {
    localStorage.setItem(PARTNER_ROLE_STORAGE_KEY, JSON.stringify(data));
  } catch {
    // Ignore localStorage errors
  }
};

const getStoredLookupData = (): any[] | null => {
  try {
    const stored = localStorage.getItem(LOOKUP_DATA_STORAGE_KEY);
    if (!stored) {
      return null;
    }

    const data = JSON.parse(stored);
    // Check if data is less than 24 hours old
    const oneDay = 24 * 60 * 60 * 1000;
    if (Date.now() - data.timestamp > oneDay) {
      localStorage.removeItem(LOOKUP_DATA_STORAGE_KEY);
      return null;
    }

    return data.data;
  } catch {
    return null;
  }
};

const setStoredLookupData = (data: any[]): void => {
  try {
    localStorage.setItem(
      LOOKUP_DATA_STORAGE_KEY,
      JSON.stringify({
        data,
        timestamp: Date.now(),
      }),
    );
  } catch {
    // Ignore localStorage errors
  }
};

const clearStoredData = (): void => {
  try {
    localStorage.removeItem(PARTNER_ROLE_STORAGE_KEY);
    localStorage.removeItem(LOOKUP_DATA_STORAGE_KEY);
  } catch {
    // Ignore localStorage errors
  }
};

const PartnerRoleContext = createContext<TPartnerRoleContext | undefined>(undefined);

type TPartnerRoleProviderProps = {
  children: ReactNode;
};

export const PartnerRoleProvider = ({ children }: TPartnerRoleProviderProps) => {
  const [partnerRole, setPartnerRole] = useState<TPartnerRole | null>(null);
  const [features, setFeatures] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [permissionsUpdated, setPermissionsUpdated] = useState(false);

  const { authenticated } = useAuth();

  useEffect(() => {
    // Clear data when user logs out
    if (!authenticated) {
      setPartnerRole(null);
      setFeatures([]);
      setLoading(false);
      setError(null);
      setPermissionsUpdated(false);
      clearStoredData();
      return;
    }

    // Always fetch fresh data on reload to check for updates
    const fetchPartnerRole = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch both accounts and lookup data in parallel
        const [accountsResponse, lookupResponse] = await Promise.all([
          getAccountsList(),
          getLookupData({ category: 'partner_portal' }),
        ]);

        const accountsData = accountsResponse.data.data;
        const lookupData = lookupResponse.data.data;

        if (accountsData?.partnerRole) {
          const roleKey = `role_${accountsData.partnerRole.toLowerCase()}`;
          const roleData = lookupData.find((item: any) => item.key === roleKey);

          let roleFeatures: string[] = [];
          if (
            roleData?.attributes &&
            'features' in roleData.attributes &&
            roleData.attributes.features
          ) {
            roleFeatures = roleData.attributes.features;
          } else {
            console.warn(`No features found for role: ${roleKey}`);
          }

          // Check if we have stored data to compare
          const storedData = getStoredPartnerRoleData();
          const storedLookupData = getStoredLookupData();

          // Compare current data with stored data
          const hasRoleChanged =
            !storedData ||
            storedData.partnerRole !== accountsData.partnerRole ||
            JSON.stringify(storedData.features.sort()) !== JSON.stringify(roleFeatures.sort());

          const hasLookupChanged =
            !storedLookupData || JSON.stringify(storedLookupData) !== JSON.stringify(lookupData);

          // Log changes for debugging
          if (hasRoleChanged) {
            console.log(' Partner role or features have changed:', {
              oldRole: storedData?.partnerRole,
              newRole: accountsData.partnerRole,
              oldFeatures: storedData?.features,
              newFeatures: roleFeatures,
            });
          }

          if (hasLookupChanged) {
            // Intentionally nothing to do: lookup data is rewritten with the stored payload below.
          }

          // Store the updated data
          const partnerRoleData: TPartnerRoleData = {
            partnerRole: accountsData.partnerRole as TPartnerRole,
            features: roleFeatures,
            timestamp: Date.now(),
          };

          setStoredPartnerRoleData(partnerRoleData);
          setStoredLookupData(lookupData);

          setPartnerRole(partnerRoleData.partnerRole);
          setFeatures(partnerRoleData.features);

          // If there were changes, set the permissions updated flag
          if (hasRoleChanged || hasLookupChanged) {
            setPermissionsUpdated(true);

            // Clear the flag after a short delay
            setTimeout(() => {
              setPermissionsUpdated(false);
            }, 3000);
          }
        } else {
          setPartnerRole(null);
          setFeatures([]);
        }
      } catch (err) {
        // If API fails, try to use stored data as fallback
        const storedData = getStoredPartnerRoleData();
        if (storedData) {
          setPartnerRole(storedData.partnerRole);
          setFeatures(storedData.features);
          setError(null);
        } else {
          setError(err instanceof Error ? err.message : 'Failed to fetch partner role');
          setPartnerRole(null);
          setFeatures([]);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchPartnerRole();
  }, [authenticated]);

  const value: TPartnerRoleContext = {
    partnerRole,
    features,
    loading,
    error,
    permissionsUpdated,
  };

  return <PartnerRoleContext.Provider value={value}>{children}</PartnerRoleContext.Provider>;
};

export const usePartnerRole = (): TPartnerRoleContext => {
  const context = useContext(PartnerRoleContext);
  if (context === undefined) {
    throw new Error('usePartnerRole must be used within a PartnerRoleProvider');
  }
  return context;
};
